import { assetStrategyDocument, readStrategyPosition, writeStrategyPosition } from '../graphql/assetStrategies';
import React, { useState, useEffect } from 'react';
import BigNumber from 'bignumber.js';
import { formatPrice, formatPercentage, formatNumberToReadable, parseFloatFromString, parseReadableNumberToFloat, formatCurrency, getDefaultCurrency, normalizeDecimalInput } from '../utils/formatters';
import { useMutation } from '@apollo/client';
import { SET_EXIT_LEVEL_STATUS, EDIT_EXIT_LEVEL, REMOVE_EXIT_LEVEL, ADD_EXIT_LEVEL } from '../graphql/mutations/exitStrategy';
import toast from 'react-hot-toast';
import Tooltip from './common/Tooltip';

const SavedExitLevels = ({ position, token, portfolioId, updateCache, refetch, assetKind = 'crypto' }) => {
  // Initialize mutation hooks
  const [setExitLevelStatus] = useMutation(assetStrategyDocument(SET_EXIT_LEVEL_STATUS, assetKind));
  const [editExitLevel] = useMutation(assetStrategyDocument(EDIT_EXIT_LEVEL, assetKind));
  const [removeExitLevel] = useMutation(assetStrategyDocument(REMOVE_EXIT_LEVEL, assetKind));
  const [addExitLevel] = useMutation(assetStrategyDocument(ADD_EXIT_LEVEL, assetKind));
  
  // State for edit/add modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [editFormData, setEditFormData] = useState({
    price: '',
    amount: '',
    status: 'PLANNED'
  });
  
  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingLevelId, setDeletingLevelId] = useState(null);
  
  // Scroll position tracking
  const [scrollPosition, setScrollPosition] = useState(0);
  
  // Handle adding/removing backdrop when modals open/close
  useEffect(() => {
    if (isEditModalOpen || isDeleteModalOpen) {
      // Create backdrop
      const backdrop = document.createElement('div');
      backdrop.id = 'modal-backdrop';
      backdrop.style.position = 'fixed';
      backdrop.style.top = '0';
      backdrop.style.left = '0';
      backdrop.style.right = '0';
      backdrop.style.bottom = '0';
      backdrop.style.width = '100%';
      backdrop.style.height = '100%';
      backdrop.style.inset = '0';
      backdrop.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
      backdrop.style.zIndex = '9998';
      
      // Add click handler to close the active modal
      backdrop.addEventListener('click', () => {
        if (isEditModalOpen) setIsEditModalOpen(false);
        if (isDeleteModalOpen) setIsDeleteModalOpen(false);
      });
      
      // Add to body
      document.body.appendChild(backdrop);
      
      // Set scroll position
      setScrollPosition(window.scrollY);
      
      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
    } else {
      // Remove backdrop if it exists
      const backdrop = document.getElementById('modal-backdrop');
      if (backdrop) {
        document.body.removeChild(backdrop);
      }
      
      // Restore body scrolling
      document.body.style.overflow = '';
    }
    
    return () => {
      // Cleanup on unmount
      const backdrop = document.getElementById('modal-backdrop');
      if (backdrop) {
        document.body.removeChild(backdrop);
      }
      document.body.style.overflow = '';
    };
  }, [isEditModalOpen, isDeleteModalOpen]);
  
  // Handle keyboard events for modals (ESC to close, Enter to submit)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isEditModalOpen) setIsEditModalOpen(false);
        if (isDeleteModalOpen) setIsDeleteModalOpen(false);
      }
    };
    
    if (isEditModalOpen || isDeleteModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditModalOpen, isDeleteModalOpen]);
  
  // Check if there are saved levels
  if (!position.exitStrategy?.levels || position.exitStrategy.levels.length === 0) {
    return null;
  }

  // Get token details for market cap calculation
  const marketData = token.marketData || {};
  const currentMarketCap = parseReadableNumberToFloat(marketData.marketCap || '0');
  const currentPrice = parseFloatFromString(marketData.currentPrice || '0');
  
  // Get position values
  const holdingsAmount = parseFloatFromString(position.holdingsAmount);

  // Calculate planned metrics based on current exit strategy
  const plannedProceeds = parseFloatFromString(position.exitStrategy?.proceeds || '0');
  const plannedCost = parseFloatFromString(position.exitStrategy?.cost || '0');
  const plannedPnlValue = plannedProceeds - plannedCost;
  const plannedRoi = plannedCost > 0 ? (plannedPnlValue / plannedCost) * 100 : 0;

  // Function to get status styles
  const getStatusStyles = (status) => {
    switch(status) {
      case 'PLANNED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'ORDER_SET':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'FILLED':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Function to cycle through statuses
  const cycleStatus = (currentStatus) => {
    switch(currentStatus) {
      case 'PLANNED':
        return 'ORDER_SET';
      case 'ORDER_SET':
        return 'FILLED';
      case 'FILLED':
        return 'PLANNED';
      default:
        return 'PLANNED';
    }
  };

  // Handle status click
  const handleStatusClick = (levelId, currentStatus) => {
    const newStatus = cycleStatus(currentStatus);
    
    setExitLevelStatus({
      variables: {
        levelId: levelId,
        status: newStatus
      },
      optimisticResponse: {
        __typename: 'Mutation',
        setExitLevelStatus: true
      },
      update: (cache, { data }) => {
        if (!data?.setExitLevelStatus) return;
        try {
          const existing = readStrategyPosition(cache, assetKind, token, portfolioId);
          if (existing?.tokenWithPosition?.position) {
            const newLevels = existing.tokenWithPosition.position.exitStrategy?.levels?.map(l => l.id === levelId ? { ...l, status: newStatus } : l) || [];
            const updated = {
              ...existing,
              tokenWithPosition: {
                ...existing.tokenWithPosition,
                position: {
                  ...existing.tokenWithPosition.position,
                  exitStrategy: {
                    ...existing.tokenWithPosition.position.exitStrategy,
                    levels: newLevels,
                  }
                },
              },
            };
            writeStrategyPosition(cache, assetKind, token, portfolioId, updated);
          }
        } catch (_) { /* ignore */ }
      },
      onCompleted: (data) => {
        if (!data?.setExitLevelStatus) {
          toast.error('Failed to update level status');
          return;
        }
        // Background refresh to update aggregates derived from exit levels
        if (typeof refetch === 'function') {
          try { refetch(); } catch (_) {}
        }
      },
      onError: (error) => {
        console.error("Error updating exit level status:", error);
        toast.error("Failed to update level status: " + error.message);
      }
    });
  };
  
  // Open edit modal
  const handleEditClick = (level, levelId) => {
    console.log("Opening edit modal for level:", level);
    const price = level.price;
    const amount = level.amount;
    console.log("Parsed values:", { price, amount });
    
    // Calculate available holdings and maximum allowed amount
    const availableAmount = calculateAvailableAmount(levelId);
    
    setIsAddMode(false);
    setEditingLevel(levelId);
    setEditFormData({
      price: price,
      amount: amount,
      status: level.status,
      maxAmount: parseFloatFromString(position.holdingsAmount),
      availableAmount: availableAmount
    });
    setIsEditModalOpen(true);
    console.log("Set modal open to true");
  };
  
  // Open add modal
  const handleAddClick = () => {
    const availableAmount = calculateAvailableAmount(null);
    
    setIsAddMode(true);
    setEditingLevel(null);
    setEditFormData({
      price: '',
      amount: '',
      status: 'PLANNED',
      maxAmount: parseFloatFromString(position.holdingsAmount),
      availableAmount: availableAmount
    });
    setIsEditModalOpen(true);
  };
  
  // Calculate available amount for this level (total holdings - other NON-FILLED levels amounts)
  const calculateAvailableAmount = (currentLevelId) => {
    // Calculate total holdings amount
    const totalHoldings = new BigNumber(position.holdingsAmount || '0');
    
    // Calculate how much is already used by other levels, excluding FILLED levels
    let usedByOtherLevels = new BigNumber(0);
    position.exitStrategy.levels.forEach((level) => {
      if (level.id !== currentLevelId && (level.status || '').toUpperCase() !== 'FILLED') {
        usedByOtherLevels = usedByOtherLevels.plus(level.amount || '0');
      }
    });
    
    return BigNumber.maximum(0, totalHoldings.minus(usedByOtherLevels)).toFixed();
  };
  
  // Handle form input changes
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    console.log(`Form field ${name} changed to:`, value);
    
    // For numeric fields, remove commas and other non-numeric characters
    let processedValue = value;
    // Normalize enum values
    if (name === 'status') {
      processedValue = (value || '').toString().trim().toUpperCase();
    }
    if (name === 'price' || name === 'amount') {
      // Remove all non-numeric characters except decimal point
      processedValue = value.replace(/[^\d.]/g, '');
      
      // If changing amount, ensure it doesn't exceed available amount
      if (name === 'amount') {
        const numericValue = new BigNumber(processedValue || '0');
        const maxAllowed = editFormData.availableAmount;
        
        if (numericValue.gt(maxAllowed)) {
          processedValue = maxAllowed.toString();
        }
      }
    }
    
    setEditFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };
  
  // Handle edit/add form submission
  const handleEditSubmit = (e) => {
    e.preventDefault();
    console.log("Submitting form with data:", editFormData, "isAddMode:", isAddMode);

    const price = new BigNumber(editFormData.price);
    const amount = new BigNumber(editFormData.amount);
    if (!price.isFinite() || !price.gt(0) || !amount.isFinite() || !amount.gt(0)) {
      toast.error('Enter a positive price and amount');
      return;
    }
    
    if (isAddMode) {
      // Add new level
      if (!editFormData.price || !editFormData.amount) {
        toast.error('Price and amount are required');
        return;
      }
      
      addExitLevel({
        variables: {
          portfolioId: portfolioId,
          tokenId: token.id,
          price: normalizeDecimalInput(editFormData.price.toString()),
          amount: normalizeDecimalInput(editFormData.amount.toString())
        },
        update: (cache, { data }) => {
          if (!data?.addExitLevel?.success) return;
          // Mark as modified when a level is added successfully
          try {
            const existing = readStrategyPosition(cache, assetKind, token, portfolioId);
            if (existing?.tokenWithPosition?.position?.exitStrategy) {
              const updated = {
                ...existing,
                tokenWithPosition: {
                  ...existing.tokenWithPosition,
                  position: {
                    ...existing.tokenWithPosition.position,
                    exitStrategy: {
                      ...existing.tokenWithPosition.position.exitStrategy,
                      isModified: true, // Mark as modified when a level is added
                    }
                  },
                },
              };
              writeStrategyPosition(cache, assetKind, token, portfolioId, updated);
            }
          } catch (_) { /* ignore */ }
        },
        onCompleted: (data) => {
          console.log("Add completed:", data);
          if (data.addExitLevel?.success) {
            setIsEditModalOpen(false);
            toast.success('Exit level added successfully');
            if (typeof refetch === 'function') {
              try { refetch(); } catch (_) {}
            }
          } else {
            const errorMsg = data.addExitLevel?.error || 'Unknown error occurred';
            console.error("Add failed:", errorMsg);
            toast.error("Failed to add exit level: " + errorMsg);
          }
        },
        onError: (error) => {
          console.error("GraphQL error adding exit level:", error);
          toast.error("Failed to add exit level: " + error.message);
        }
      });
    } else {
      // Edit existing level
      const levelId = editingLevel;
      
      // Create an updates object with only the fields that have changed
      const updates = {};
      if (editFormData.price !== '') updates.price = normalizeDecimalInput(editFormData.price.toString());
      if (editFormData.amount !== '') updates.amount = normalizeDecimalInput(editFormData.amount.toString());
      if (editFormData.status) {
        const normalizedStatus = (editFormData.status || '').toString().trim().toUpperCase();
        const allowedStatuses = new Set(['PLANNED', 'ORDER_SET', 'FILLED']);
        if (allowedStatuses.has(normalizedStatus)) {
          updates.status = normalizedStatus;
        }
      }
      
      console.log("Sending updates to API:", updates, "for levelId:", levelId);
      
      editExitLevel({
        variables: {
          levelId: levelId,
          updates
        },
        update: (cache, { data }) => {
          if (!data?.editExitLevel?.success) return;
          try {
            const existing = readStrategyPosition(cache, assetKind, token, portfolioId);
            if (existing?.tokenWithPosition?.position) {
              // Replace the level inline using values from the edit form
              const newLevels = (existing.tokenWithPosition.position.exitStrategy?.levels || []).map(l => 
                l.id === levelId 
                  ? { 
                      ...l, 
                      price: updates.price ?? l.price,
                      amount: updates.amount ?? l.amount,
                      status: updates.status ?? l.status,
                    }
                  : l
              );
              const updated = {
                ...existing,
                tokenWithPosition: {
                  ...existing.tokenWithPosition,
                  position: {
                    ...existing.tokenWithPosition.position,
                    exitStrategy: {
                      ...existing.tokenWithPosition.position.exitStrategy,
                      levels: newLevels,
                      isModified: true, // Mark as modified when levels are edited
                    }
                  },
                },
              };
              writeStrategyPosition(cache, assetKind, token, portfolioId, updated);
            }
          } catch (_) { /* ignore */ }
        },
        onCompleted: (data) => {
          console.log("Edit completed:", data);
          if (data.editExitLevel?.success) {
            setIsEditModalOpen(false);
            toast.success('Exit level updated successfully');
            if (typeof refetch === 'function') {
              try { refetch(); } catch (_) {}
            }
          } else {
            const errorMsg = data.editExitLevel?.error || 'Unknown error occurred';
            console.error("Edit failed:", errorMsg);
            toast.error("Failed to update exit level: " + errorMsg);
          }
        },
        onError: (error) => {
          console.error("GraphQL error updating exit level:", error);
          toast.error("Failed to update exit level: " + error.message);
        }
      });
    }
  };
  
  // Open delete confirmation modal
  const handleDeleteClick = (levelId) => {
    setDeletingLevelId(levelId);
    setIsDeleteModalOpen(true);
  };
  
      // Handle delete confirmation
    const handleDeleteConfirm = () => {
      const levelId = deletingLevelId;
    
    removeExitLevel({
      variables: {
        levelId: levelId
      },
      update: (cache, { data }) => {
        if (!data?.removeExitLevel) return;
        try {
          const existing = readStrategyPosition(cache, assetKind, token, portfolioId);
          if (existing?.tokenWithPosition?.position) {
            const newLevels = (existing.tokenWithPosition.position.exitStrategy?.levels || []).filter(l => l.id !== levelId);
            const updated = {
              ...existing,
              tokenWithPosition: {
                ...existing.tokenWithPosition,
                position: {
                  ...existing.tokenWithPosition.position,
                  exitStrategy: {
                    ...existing.tokenWithPosition.position.exitStrategy,
                    levels: newLevels,
                    isModified: true, // Mark as modified when a level is removed
                  }
                },
              },
            };
            writeStrategyPosition(cache, assetKind, token, portfolioId, updated);
          }
        } catch (_) { /* ignore */ }
      },
      onCompleted: (data) => {
        console.log("Delete completed:", data);
        if (!data?.removeExitLevel) {
          toast.error('Failed to delete exit level');
          return;
        }
        setIsDeleteModalOpen(false);
        setDeletingLevelId(null);
        toast.success('Exit level deleted successfully');
        if (typeof refetch === 'function') {
          try { refetch(); } catch (_) {}
        }
      },
      onError: (error) => {
        console.error("Error deleting exit level:", error);
        toast.error("Failed to delete exit level: " + error.message);
        setIsDeleteModalOpen(false);
        setDeletingLevelId(null);
      }
    });
  };

  const sortedLevels = [...position.exitStrategy.levels]
    .sort((a, b) => parseFloatFromString(a.price) - parseFloatFromString(b.price));

  const getLevelMetrics = (level) => {
    const levelPrice = parseFloatFromString(level.price);
    const levelAmount = parseFloatFromString(level.amount);
    const isFilled = (level.status || '').toUpperCase() === 'FILLED';
    const levelPercentage = !isFilled && holdingsAmount > 0
      ? (levelAmount / holdingsAmount) * 100
      : null;
    const levelValue = levelAmount * levelPrice;
    let estimatedMarketCap = 0;
    if (currentPrice > 0 && currentMarketCap > 0) {
      estimatedMarketCap = (levelPrice / currentPrice) * currentMarketCap;
    }
    return { levelPrice, levelAmount, isFilled, levelPercentage, levelValue, estimatedMarketCap };
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-2">
      <div className="space-y-2 md:hidden">
        {sortedLevels.map((level, index) => {
          const { levelPrice, levelAmount, isFilled, levelPercentage, levelValue, estimatedMarketCap } = getLevelMetrics(level);
          return (
            <div key={level.id} className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/60 p-2 text-xs">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Exit L{index + 1}</div>
                  <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {formatPrice(levelPrice)}
                    {!isFilled && currentPrice > 0 && (
                      currentPrice > levelPrice ? (
                        <span className="ml-2 inline-block h-2 w-2 rounded-full bg-red-500" title="Price is above level" />
                      ) : currentPrice >= levelPrice * 0.9 ? (
                        <span className="ml-2 inline-block h-2 w-2 rounded-full bg-yellow-500" title="Price is within 10% of level" />
                      ) : null
                    )}
                  </div>
                </div>
                <span
                  className={`shrink-0 px-2 py-1 rounded-md text-[11px] font-medium cursor-pointer ${getStatusStyles(level.status)}`}
                  onClick={() => handleStatusClick(level.id, level.status)}
                >
                  {level.status || 'PLANNED'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-gray-700 dark:text-gray-200">
                <div><span className="text-gray-500 dark:text-gray-400">Amount</span><br />{levelAmount}</div>
                <div><span className="text-gray-500 dark:text-gray-400">Value</span><br />{formatPrice(levelValue)}</div>
                <div><span className="text-gray-500 dark:text-gray-400">Holdings</span><br />{isFilled ? '-' : formatPercentage(levelPercentage)}</div>
                <div><span className="text-gray-500 dark:text-gray-400">MCap</span><br />{formatNumberToReadable(estimatedMarketCap)}</div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={() => handleEditClick(level, level.id)}
                  className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-xs font-medium"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteClick(level.id)}
                  className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded-md text-xs font-medium"
                >
                  Delete
                </button>
              </div>
              {level.warning && (
                <div className="mt-2 rounded bg-yellow-50 dark:bg-yellow-900/20 px-2 py-1 text-yellow-700 dark:text-yellow-400">
                  {level.warning}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b dark:border-gray-700">
              <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">Status</th>
              <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">Price</th>
              <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">Market Cap</th>
              <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">% of Holdings</th>
              <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">Amount</th>
              <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">Value</th>
              <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedLevels.map((level, index) => {
              const { levelPrice, levelAmount, isFilled, levelPercentage, levelValue, estimatedMarketCap } = getLevelMetrics(level);
              
              return (
                <React.Fragment key={level.id}>
                  <tr className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-150">
                    <td className="py-2">
                      <span 
                        className={`px-2 py-1 rounded-md text-xs font-medium cursor-pointer ${getStatusStyles(level.status)}`}
                        onClick={() => handleStatusClick(level.id, level.status)}
                      >
                        {level.status || 'PLANNED'}
                      </span>
                    </td>
                    <td className="py-2 text-gray-800 dark:text-gray-200">
                      <span className="flex items-center gap-1">
                        {formatPrice(levelPrice)}
                        {!isFilled && currentPrice > 0 && (
                          currentPrice > levelPrice ? (
                            <span className="inline-block w-2 h-2 rounded-full bg-red-500" title="Price is above level" />
                          ) : currentPrice >= levelPrice * 0.9 ? (
                            <span className="inline-block w-2 h-2 rounded-full bg-yellow-500" title="Price is within 10% of level" />
                          ) : null
                        )}
                      </span>
                    </td>
                    <td className="py-2 text-gray-800 dark:text-gray-200">{formatNumberToReadable(estimatedMarketCap)}</td>
                    <td className="py-2 text-gray-800 dark:text-gray-200">{isFilled ? '-' : formatPercentage(levelPercentage)}</td>
                    <td className="py-2 text-gray-800 dark:text-gray-200">{levelAmount}</td>
                    <td className="py-2 text-gray-800 dark:text-gray-200">{formatPrice(levelValue)}</td>
                    <td className="py-2 flex space-x-2">
                      <button
                        onClick={() => handleEditClick(level, level.id)}
                        className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-xs font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(level.id)}
                        className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded-md text-xs font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                  {level.warning && (
                    <tr className="bg-yellow-50 dark:bg-yellow-900/20">
                      <td colSpan="7" className="py-2 px-3 text-yellow-700 dark:text-yellow-400 text-sm">
                        <div className="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <span>{level.warning}</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        
        {/* Add Level button */}
        <div className="mt-2">
          <button
            onClick={handleAddClick}
            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add level
          </button>
        </div>
      </div>
      <div className="mt-2 md:hidden">
        <button
          onClick={handleAddClick}
          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add level
        </button>
      </div>
      
      {/* Exit strategy summary */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-1 gap-4">
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Planned to sell:</p>
              <p className="text-lg font-medium text-gray-800 dark:text-gray-200">{position.exitStrategy.sellAmount} {token?.ticker || 'TOKEN'}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{((parseFloatFromString(position.exitStrategy.sellAmount) / holdingsAmount) * 100).toFixed(1)}% of holdings</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Investment cost:</p>
              <p className="text-lg font-medium text-gray-800 dark:text-gray-200">{formatCurrency(position.exitStrategy.cost, getDefaultCurrency())}</p>
              <Tooltip
                title="FIFO (First In, First Out) means we match the tokens you're selling with your earliest purchases first. This gives you an accurate cost basis for calculating your profit/loss on this exit plan."
                position="bottom"
                trigger="mouseenter"
                arrow={true}
                size="small"
              >
                <p className="text-sm text-gray-600 dark:text-gray-400 cursor-help underline decoration-dotted underline-offset-2">FIFO cost basis</p>
              </Tooltip>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Planned proceeds:</p>
              <p className="text-lg font-medium text-gray-800 dark:text-gray-200">{formatCurrency(position.exitStrategy.proceeds)}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{formatPrice(parseFloatFromString(position.exitStrategy.proceeds) / parseFloatFromString(position.exitStrategy.sellAmount))} avg. sell price</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Planned PnL:</p>
              <p className="text-lg font-medium text-green-600 dark:text-green-400">{formatCurrency(plannedPnlValue, getDefaultCurrency())}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{plannedRoi.toFixed(1)}% ROI ({(plannedRoi / 100 + 1).toFixed(1)}x)</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Edit Modal */}
      {isEditModalOpen && (
        <div 
          className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full m-4"
          style={{
            position: 'absolute',
            top: `calc(50vh + ${scrollPosition}px)`,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxHeight: '80vh',
            overflowY: 'auto',
            zIndex: 9999,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
        >
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
            {isAddMode ? 'Add Exit Level' : 'Edit Exit Level'}
          </h2>
          
          <form onSubmit={handleEditSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700 dark:text-gray-300 mb-2" htmlFor="price">
                Target Price
              </label>
              <input
                type="text"
                inputMode="decimal"
                id="price"
                name="price"
                value={editFormData.price}
                onChange={handleEditFormChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter target price"
                autoFocus
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 dark:text-gray-300 mb-2" htmlFor="amount">
                Amount to Sell
              </label>
              <div className="flex flex-col space-y-2">
                <input
                  type="text"
                  inputMode="decimal"
                  id="amount"
                  name="amount"
                  value={editFormData.amount}
                  onChange={handleEditFormChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Enter amount (max: ${new BigNumber(editFormData.availableAmount || '0').toFixed(6)})`}
                />
                
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">0</span>
                  <input
                    type="range"
                    id="amount-slider"
                    name="amount"
                    min="0"
                    max={editFormData.availableAmount || 0}
                    step={Math.min(0.000001, (editFormData.availableAmount || 0) / 100)}
                    value={editFormData.amount || 0}
                    onChange={handleEditFormChange}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new BigNumber(editFormData.availableAmount || '0').toFixed(6) || 0}
                  </span>
                </div>
                
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    Available: {new BigNumber(editFormData.availableAmount || '0').toFixed(6) || 0} {token?.ticker || 'TOKEN'}
                  </span>
                  <span>
                    {((parseFloatFromString(editFormData.amount) / parseFloatFromString(position.holdingsAmount)) * 100).toFixed(1)}% of holdings
                  </span>
                </div>
              </div>
            </div>
            
            {!isAddMode && (
              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 mb-2" htmlFor="status">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={editFormData.status}
                  onChange={handleEditFormChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="PLANNED">Planned</option>
                  <option value="ORDER_SET">Order Set</option>
                  <option value="FILLED">Filled</option>
                </select>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  console.log("Closing modal");
                  setIsEditModalOpen(false);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div 
          className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full m-4"
          style={{
            position: 'absolute',
            top: `calc(50vh + ${scrollPosition}px)`,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxHeight: '80vh',
            overflowY: 'auto',
            zIndex: 9999,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleDeleteConfirm();
            }
          }}
          tabIndex={-1}
          ref={(el) => el && el.focus()}
        >
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Confirm Deletion
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Are you sure you want to delete this level? This action cannot be undone.
          </p>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                console.log("Cancel delete, closing modal");
                setIsDeleteModalOpen(false);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
              autoFocus
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedExitLevels; 